import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { io } from 'socket.io-client';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as React from "react";
import { API_URL } from "../config";


// Icons
import {
  Eye, EyeOff, Edit2, Mail, Calendar, AlertTriangle,
  Users, Search, Ban, Trash2, UserCheck, UserX,
  Phone, MapPin, Package, X,
} from 'lucide-react';

interface Order {
  id: number;
  orderNo: string;
  orderRef: string;
  status: string;
  orderDate: string;
  jobType?: string;
}

interface Client {
  userId: number;
  name: string;
  email: string;
  role: string;
  isBlocked: boolean;
  status?: 'pending' | 'approved' | 'rejected';
  createdAt?: string;
  phone?: string;
  address?: string;
  password?: string;
  notes?: string;
  postcode?: string;
  city?: string;
  orders: Order[];
}

interface PendingUser {
  id: number;
  name: string;
  email: string;
  createdAt: string;
  postcode?: string;
  city?: string;
}

interface DeleteConfirmation {
  type: 'order' | 'client';
  id: number;
  name: string;
}


// Adaugă la început cu celelalte interfețe
interface NewClientData {
  name: string;
  email: string;
  password: string;
  phone?: string;
  address?: string;
}

interface EditableFieldProps {
  label: string;
  value?: string;
  field?: keyof Client;
  clientId?: number;
  onSave?: (field: keyof Client, value: string) => void;
  isPassword?: boolean;
  isTextarea?: boolean;
  inputComponent?: (props: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }) => React.ReactElement;
}



interface UpdateClientResponse {
  id: number;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  postcode?: string;
  city?: string;
  password?: string;
  notes?: string;
  [key: string]: any; // pentru flexibilitate
}




const EditableField = ({ label, value, field, clientId, onSave, isPassword = false, isTextarea = false, inputComponent }: EditableFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newValue, setNewValue] = useState(value || "");
  const [showCreateClientPassword, setShowCreateClientPassword] = useState(false);




  const handleSave = () => {
    if (onSave && field) {
      onSave(field, newValue);
      setIsEditing(false);
    }
  };

  return (
    <div className="flex items-start justify-between mb-3">
      <span className="font-medium text-gray-700 dark:text-gray-300 mt-1">{label}:</span>
      <div className="flex items-start space-x-2 flex-1 ml-4">
        {isEditing && onSave && field ? (
          <>
            <div className="flex-1 relative">
              {isTextarea ? (
                <textarea
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="w-full px-2 py-1 border rounded-lg text-sm dark:bg-neutral-600 resize-none"
                  rows={3}
                />
              ) : inputComponent ? (
                inputComponent({
                  value: newValue,
                  onChange: (e) => setNewValue(e.target.value)
                })
              ) : (
                <input
                  type={isPassword ? (showCreateClientPassword ? "text" : "password") : "text"}
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="w-full px-2 py-1 border rounded-lg text-sm dark:bg-neutral-600"
                />
              )}
            </div>
            <button onClick={handleSave} className="text-green-600 hover:text-green-800 text-sm p-1">✓</button>
            <button onClick={() => setIsEditing(false)} className="text-gray-400 hover:text-gray-600 text-sm p-1">✕</button>
          </>
        ) : (
          <>
            <div className="flex-1">
              {isPassword ? (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-900 dark:text-white text-sm">
                    {showCreateClientPassword ? (value || "Not set") : "••••••••"}
                  </span>
                  <button
                    onClick={() => setShowCreateClientPassword(!showCreateClientPassword)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {showCreateClientPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              ) : isTextarea ? (
                value ? (
                  <div className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap bg-gray-50 dark:bg-neutral-600 p-2 rounded max-h-20 overflow-y-auto">
                    {value}
                  </div>
                ) : (
                  <span className="text-sm text-gray-400 italic">Fără notițe</span>
                )
              ) : (
                <span className="text-gray-900 dark:text-white text-sm">{value || "Not provided"}</span>
              )}
            </div>
            {onSave && field && (
              <button onClick={() => setIsEditing(true)} className="text-gray-400 hover:text-gray-600 p-1">
                <Edit2 className="w-4 h-4" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};






const UKPhoneInput = ({ value, onChange }: { value: string; onChange: (val: string) => void }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/\D/g, ""); // păstrează doar cifre

    // elimină 0-ul de la început dacă există
    if (input.startsWith("44")) input = input.slice(2);
    if (input.startsWith("0")) input = input.slice(1);

    // limitează la maxim 10 cifre după prefix
    input = input.slice(0, 10);

    // formatare +44 XXXX XXXXXX
    let formatted = "+44 ";
    if (input.length <= 4) {
      formatted += input;
    } else {
      formatted += input.slice(0, 4) + " " + input.slice(4);
    }

    onChange(formatted);
  };

  return (
    <input
      type="text"
      value={value}
      onChange={handleChange}
      placeholder="+44 XXXX XXXXXX"
      className="w-full px-3 py-2 border border-gray-300 dark:bg-neutral-600 rounded-lg"
    />
  );
};




// Client Details Modal Component
const ClientDetailsModal = ({ client, onClose, onBlock, onDelete }: {
  client: Client;
  onClose: () => void;
  onBlock: (clientId: number) => void;
  onDelete: (clientId: number) => void;
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold flex items-center" data-testid="heading-client-details">
          <Eye className="w-5 h-5 mr-2" />
          Client Details
        </h2>

        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
          data-testid="button-close-details"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="bg-neutral-50 dark:bg-neutral-700 p-4 rounded-lg">
          <h3 className="font-semibold mb-3 flex items-center">
            <Mail className="w-4 h-4 mr-2" />
            Personal Information
          </h3>
          <div className="space-y-2 text-sm">
            <div data-testid="text-client-name"><span className="font-medium">Name:</span> {client.name}</div>
            <div data-testid="text-client-email"><span className="font-medium">Email:</span> {client.email}</div>
            <div data-testid="text-client-phone">
              <span className="font-medium flex items-center">
                <Phone className="w-4 h-4 mr-1" />
                Phone:
              </span>
              {client.phone || 'Not provided'}
            </div>
            <div data-testid="text-client-address">
              <span className="font-medium flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                Address:
              </span>
              {client.address || 'Not provided'}
            </div>
            <div data-testid="text-client-role"><span className="font-medium">Role:</span> {client.role}</div>
            <div data-testid="text-client-member-since">
              <span className="font-medium flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                Member since:
              </span>
              {client.createdAt ? new Date(client.createdAt).toLocaleDateString() : 'Unknown'}
            </div>
            <div>
              <span className="font-medium">Status:</span>
              <span className={`ml-2 px-2 py-1 rounded-full text-xs ${client.isBlocked
                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                }`} data-testid="badge-client-status">
                {client.isBlocked ? 'Blocked' : 'Active'}
              </span>
            </div>
          </div>
        </div>

        {/* Orders Summary */}
        <div className="bg-neutral-50 dark:bg-neutral-700 p-4 rounded-lg">
          <h3 className="font-semibold mb-3 flex items-center">
            <Package className="w-4 h-4 mr-2" />
            Orders Summary
          </h3>
          <div className="space-y-2 text-sm">
            <div data-testid="text-total-orders"><span className="font-medium">Total Orders:</span> {client.orders.length}</div>
            <div data-testid="text-latest-order"><span className="font-medium">Latest Order:</span> {
              client.orders.length > 0
                ? client.orders[0].orderRef
                : 'No orders'
            }</div>
            <div data-testid="text-latest-status"><span className="font-medium">Latest Status:</span> {
              client.orders.length > 0
                ? client.orders[0].status
                : 'N/A'
            }</div>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      {client.orders.length > 0 && (
        <div className="mt-6">
          <h3 className="font-semibold mb-3">Recent Orders</h3>
          <div className="bg-neutral-50 dark:bg-neutral-700 rounded-lg overflow-hidden">
            <div className="max-h-40 overflow-y-auto">
              {client.orders.slice(0, 5).map((order) => (
                <div key={order.id} className="flex justify-between items-center p-3 border-b border-gray-200 dark:border-neutral-600 last:border-b-0" data-testid={`order-item-${order.id}`}>
                  <div>
                    <div className="font-medium text-sm">{order.orderRef}</div>
                    <div className="text-xs text-gray-500">{order.jobType}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs px-2 py-1 rounded-full ${order.status === 'Dispatched' ? 'bg-green-100 text-green-800' :
                      order.status === 'In process' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                      {order.status}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{new Date(order.orderDate).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-neutral-600">
        <button
          onClick={() => onBlock(client.userId)}
          className={`px-4 py-2 rounded-lg transition-colors ${client.isBlocked
            ? 'bg-green-500 hover:bg-green-600 text-white'
            : 'bg-yellow-500 hover:bg-yellow-600 text-white'
            }`}
          data-testid="button-block-unblock-client"
        >
          <Ban className="w-4 h-4 mr-2 inline" />
          {client.isBlocked ? 'Unblock' : 'Block'}
        </button>

        <button
          onClick={() => onDelete(client.userId)}
          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          data-testid="button-delete-client-modal"
        >
          <Trash2 className="w-4 h-4 mr-2 inline" />
          Delete
        </button>

        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
          data-testid="button-close-client-details"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

const ManageClients = () => {
  const [selectedTab, setSelectedTab] = useState<'clients' | 'newClients' | 'deleteRequests'>('clients');
  const [deleteRequests, setDeleteRequests] = useState<any[]>([]);

  const [clients, setClients] = useState<Client[]>([]);
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation | null>(null);
  const [showClientDetails, setShowClientDetails] = useState<Client | null>(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [isApproving, setIsApproving] = useState<number | null>(null);
  const [isRejecting, setIsRejecting] = useState<number | null>(null);
  const navigate = useNavigate();
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [newClientData, setNewClientData] = useState<NewClientData>({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: ''
  });
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // 🔥 pentru toggle password




  // Adaugă la celelalte useState-uri
  const [selectedPendingUser, setSelectedPendingUser] = useState<PendingUser | null>(null);

  const [selectedDeleteRequest, setSelectedDeleteRequest] = useState<any | null>(null);


  const handleUpdateClient = async (field: keyof Client, value: string) => {
    if (!selectedClient) return;

    try {
      const token = localStorage.getItem("token");

      const updatePayload = { [field]: value };

      const response = await axios.put<UpdateClientResponse>(
        `${API_URL}/admin/clients/${selectedClient.userId}`,
        updatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedClient = response.data;

      if (field === 'password') {
        setSelectedClient((prev) =>
          prev ? { ...prev, password: '' } : prev
        );

        setClients((prev) =>
          prev.map((c) =>
            c.userId === selectedClient.userId
              ? { ...c, password: '' }
              : c
          )
        );

        toast.success("Password updated successfully!");

      } else {
        setSelectedClient((prev) =>
          prev ? { ...prev, [field]: updatedClient[field] || value } : prev
        );

        setClients((prev) =>
          prev.map((c) =>
            c.userId === selectedClient.userId
              ? { ...c, [field]: updatedClient[field] || value }
              : c
          )
        );

        toast.success(`${field} updated successfully!`);

      }
    } catch (error) {
      console.error("Error updating client:", error);
      toast.error("Error updating client info");

    }
  };



  // Check authentication and redirect
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/auth");  // ← Acum funcționează
      return;
    }

  }, [navigate]);




  // Fetch clients data
  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get<any[]>(
        `${API_URL}/admin/all-approved-users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );


      console.log('Raw response data:', res.data);

      const clientsMap: { [key: number]: Client } = {};

      res.data.forEach((row: any) => {
        const uid = row.userid || row.userId; // ÎNCEARCĂ AMBELE VARIANTE

        console.log('Row userId:', uid, 'Row data:', row); // DEBUG

        if (!clientsMap[uid]) {
          clientsMap[uid] = {
            userId: uid,
            name: row.name,
            email: row.email,
            role: row.role,
            isBlocked: row.isblocked || row.isBlocked || false,
            status: row.status || 'approved',
            createdAt: row.createdat || row.createdAt,
            phone: row.phone,
            address: row.address,
            postcode: row.postcode,
            city: row.city,
            password: row.password || '',
            notes: row.notes,
            orders: [],
          };
        }

        // Adaugă comenzi doar dacă există
        if (row.orderid && row.orderref) {
          clientsMap[uid].orders.push({
            id: row.orderid,
            orderNo: row.orderno || '',
            orderRef: row.orderref,
            status: row.orderstatus,
            orderDate: row.orderdate,
            jobType: row.jobtype || '',
          });
        }
      });

      const clientsArray = Object.values(clientsMap);
      console.log('Final clients array:', clientsArray);

      setClients(clientsArray);
      setFilteredClients(clientsArray);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  }, []);



  // Fetch pending users
  const fetchPendingUsers = useCallback(async () => {
    setPendingLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get<PendingUser[]>(
        `${API_URL}/admin/users/pending`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPendingUsers(res.data);
    } catch (error) {
      console.error("Error fetching pending users:", error);
      setPendingUsers([]);
    } finally {
      setPendingLoading(false);
    }
  }, []);



  const fetchDeleteRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get<any[]>(
        `${API_URL}/admin/delete-requests`,
        { headers: { Authorization: `Bearer ${token}` } }
      );


      // Luăm comenzile tuturor utilizatorilor
      const ordersRes = await axios.get<any[]>(
        `${API_URL}/admin/all-users-orders`,
        { headers: { Authorization: `Bearer ${token}` } }
      );


      const allOrders = ordersRes.data;

      setDeleteRequests(
        res.data.map((req) => ({
          ...req,
          userId: req.user_id,
          orders: allOrders.filter((order) => order.userid === req.user_id), // 🔥 atașăm comenzile utilizatorului
        }))
      );
    } catch (error) {
      console.error("Error fetching delete requests:", error);
      setDeleteRequests([]);
    }
  }, []);





  // Initialize data
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      fetchClients();
      fetchPendingUsers();
      fetchDeleteRequests();
    }
  }, [fetchClients, fetchPendingUsers, fetchDeleteRequests]);



  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    // Socket pentru notificări admin
    const newSocket = io(API_URL.replace('/api', ''), {
      path: '/ws',
      auth: { token },
    });

    // Listener pentru când clientul își modifică profilul
    newSocket.on('clientProfileUpdated', (data: any) => {
      console.log('Client profile updated:', data);

      // Actualizează lista de clienți în timp real
      setClients(prevClients =>
        prevClients.map(client =>
          client.userId === data.userId
            ? { ...client, ...data.client }
            : client
        )
      );
      // Actualizează și clientul selectat dacă e cazul
      setSelectedClient(prevSelected =>
        prevSelected && prevSelected.userId === data.userId
          ? { ...prevSelected, ...data.client }
          : prevSelected
      );

      // Actualizează și modal-ul de detalii dacă e deschis
      setShowClientDetails(prevDetails =>
        prevDetails && prevDetails.userId === data.userId
          ? { ...prevDetails, ...data.client }
          : prevDetails
      );
    });

    return () => {
      newSocket.disconnect();
    };
  }, []);


  // Filter clients based on search
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client =>
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.orders.some(order =>
          order.orderRef.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.orderNo.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredClients(filtered);
    }
  }, [searchTerm, clients]);

  // Handle approve user
  const handleApproveUser = async (userId: number) => {
    setIsApproving(userId);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/admin/users/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh data
      await fetchPendingUsers();
      setSelectedPendingUser(null);
      await fetchClients();

      toast.success("User approved successfully!");

    } catch (error) {
      console.error("Error approving user:", error);
      // ❌ Mesaj de eroare
      toast.error("Error approving user");
    } finally {
      setIsApproving(null);
    }
  };


  // Handle reject user
  const handleRejectUser = async (userId: number) => {
    setIsRejecting(userId);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/admin/users/${userId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh pending users
      await fetchPendingUsers();
      setSelectedPendingUser(null);

      // ✅ Mesaj de succes
      toast.success("User rejected successfully!");
    } catch (error) {
      console.error("Error rejecting user:", error);
      // ❌ Mesaj de eroare
      toast.error("Error rejecting user");
    } finally {
      setIsRejecting(null);
    }
  };



  // Handle block/unblock client
  const handleBlockClient = async (clientId: number) => {
    setIsBlocking(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/admin/clients/${clientId}/block`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      setClients(prev => prev.map(client =>
        client.userId === clientId ? { ...client, isBlocked: !client.isBlocked } : client
      ));

      setSelectedClient(prev => prev && prev.userId === clientId ?
        { ...prev, isBlocked: !prev.isBlocked } : prev
      );

      // Update details modal if open
      if (showClientDetails?.userId === clientId) {
        setShowClientDetails(prev => prev ? { ...prev, isBlocked: !prev.isBlocked } : null);
      }

      // ✅ Mesaj de succes
      toast.success("Client status updated successfully!");

    } catch (error) {
      console.error("Error blocking client:", error);
      // ❌ Mesaj de eroare
      toast.error("Error blocking client");
    } finally {
      setIsBlocking(false);
    }
  };


  // Adaugă această funcție după handleRejectUser
  const handleCreateClient = async () => {
    if (!newClientData.name || !newClientData.email || !newClientData.password) {
      toast.error('Name, email și password sunt obligatorii!');
      return;
    }

    setIsCreatingClient(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/admin/create-client`, {
        name: newClientData.name,
        email: newClientData.email,
        password: newClientData.password,
        phone: newClientData.phone,
        address: newClientData.address
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Reset form și închide modal
      setNewClientData({ name: '', email: '', password: '', phone: '', address: '' });
      setShowCreateClientModal(false);

      // Refresh data
      await fetchClients();

      toast.success('Client creat cu succes!');
    } catch (error: any) {
      console.error('Error creating client:', error);
      toast.error(error.response?.data?.message || 'Eroare la crearea clientului');
    } finally {
      setIsCreatingClient(false);
    }
  };



  // Handle delete client
  const handleDeleteClient = async (clientId: number) => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/admin/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Scoate clientul din lista principală
      setClients(prev => prev.filter(client => client.userId !== clientId));

      // Scoate cererea de delete din lista de requests
      setDeleteRequests(prev => prev.filter(req => req.userId !== clientId));

      // Resetează selecția
      setSelectedClient(null);
      setSelectedDeleteRequest(null);
      setShowClientDetails(null);
      setDeleteConfirmation(null);

      // ✅ Notificare succes
      toast.success("Client șters cu succes!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });

    } catch (error: any) {
      console.error("Error deleting client:", error);

      // ⚠️ Notificare eroare
      toast.error(error.response?.data?.message || "Eroare la ștergerea clientului", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // 🆕 Stergere order (Admin)
  const handleDeleteOrder = async (orderId: number, userId: number) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/admin/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // actualizează selectedClient (dacă e deschis în UI)
      if (selectedClient) {
        const updatedOrders = selectedClient.orders.filter((o) => o.id !== orderId);
        setSelectedClient({ ...selectedClient, orders: updatedOrders });
      }

      // actualizează lista principală de clients
      setClients((prev) =>
        prev.map((c) =>
          c.userId === userId ? { ...c, orders: c.orders.filter((o) => o.id !== orderId) } : c
        )
      );

      // ✅ Notificare succes
      toast.success("Comanda a fost ștearsă cu succes!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });

    } catch (error: any) {
      console.error("Error deleting order:", error);

      // ⚠️ Notificare eroare
      toast.error(error.response?.data?.message || "Nu s-a putut șterge comanda.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });
    }
  };




  if (loading || pendingLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#025951]"></div>
            <span className="text-lg text-gray-700 dark:text-gray-300">Loading clients...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2 flex items-center">
            <Users className="w-6 h-6 mr-2" />
            Manage Clients
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage client accounts and pending registrations
          </p>
        </div>



        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-gray-100 dark:bg-neutral-600 p-1 rounded-lg w-fit">
          <button
            data-testid="tab-clients"
            onClick={() => setSelectedTab('clients')}
            className={`px-6 py-2 rounded-md transition-all ${selectedTab === 'clients'
              ? 'bg-white dark:bg-neutral-800 text-[#025951] font-semibold shadow-sm'
              : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
              }`}
          >
            Clients ({clients.length})
          </button>
          <button
            data-testid="tab-new-clients"
            onClick={() => setSelectedTab('newClients')}
            className={`px-6 py-2 rounded-md transition-all ${selectedTab === 'newClients'
              ? 'bg-white dark:bg-neutral-800 text-[#025951] font-semibold shadow-sm'
              : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
              }`}
          >
            New Clients ({pendingUsers.length})
            {pendingUsers.length > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                {pendingUsers.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setSelectedTab('deleteRequests')}
            className={`px-6 py-2 rounded-md transition-all ${selectedTab === 'deleteRequests'
              ? 'bg-white dark:bg-neutral-800 text-red-600 font-semibold shadow-sm'
              : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
              }`}
          >
            Delete Requests ({deleteRequests.length})
            {deleteRequests.length > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                {deleteRequests.length}
              </span>
            )}
          </button>

        </div>

        {/* Search Bar - doar pentru Clients */}
        {selectedTab === 'clients' && (
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                data-testid="input-search-clients"
                type="text"
                placeholder="Search clients by name, email, or order reference..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-green-100 dark:bg-neutral-700 border border-gray-300 dark:border-neutral-500 rounded-lg focus:ring-2 focus:ring-[#025951] focus:border-transparent"
              />
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">

          {/* Clients/New Clients List */}
          <div className="w-full lg:w-1/3">
            <div className="bg-white dark:bg-neutral-700 rounded-2xl shadow-lg border border-gray-200 dark:border-neutral-500 p-6 max-h-[80vh] overflow-y-auto">
              {/* Buton Create Client - doar pentru New Clients tab */}
              {selectedTab === 'newClients' && (
                <div className="space-y-3">

                  {/* CARD CREATE CLIENT */}
                  <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold text-blue-900 dark:text-blue-100">Create New Client</h3>
                      <p className="text-sm text-blue-700 dark:text-blue-300">Create a client account manually</p>
                    </div>
                    <button
                      onClick={() => setShowCreateClientModal(true)}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Create Client
                    </button>
                  </div>

                  {/* LISTA PENDING USERS */}
                  {pendingUsers.map((user) => (
                    <div
                      key={user.id}
                      className="cursor-pointer p-3 rounded-lg border transition-all duration-200 hover:scale-[1.02]"
                      onClick={() => setSelectedPendingUser(user)}
                    >
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  ))}

                </div>
              )}


              <h2 className="text-lg font-semibold mb-4">
                {selectedTab === 'clients'
                  ? `Clients (${filteredClients.length})`
                  : `New Clients Awaiting Approval (${pendingUsers.length})`
                }
              </h2>

              {/* LISTA DIN STÂNGA */}
              {selectedTab === 'clients' && (
                <div className="space-y-3">
                  {filteredClients.map((client) => (
                    <div
                      key={client.userId}
                      data-testid={`client-card-${client.userId}`}
                      className={`cursor-pointer p-3 rounded-lg border transition-all duration-200 hover:scale-[1.02] ${selectedClient?.userId === client.userId
                        ? "bg-gradient-to-r from-[#025951] to-emerald-500 text-white border-[#025951] shadow-lg"
                        : "bg-neutral-50 dark:bg-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-500 border-gray-200 dark:border-neutral-500 text-gray-700 dark:text-gray-300"
                        } ${client.isBlocked ? "border-l-4 !border-l-red-500" : ""}`}
                      onClick={() => setSelectedClient(client)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{client.name}</div>
                          <div className={`text-xs truncate ${selectedClient?.userId === client.userId ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'
                            }`}>
                            {client.email}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-2">
                          <span className={`text-xs ${selectedClient?.userId === client.userId ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'
                            }`}>
                            {client.orders.length} orders
                          </span>
                          {client.isBlocked && (
                            <span className="bg-red-500 text-white text-xs font-bold px-1 py-0.5 rounded-full">
                              BLOCKED
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {filteredClients.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p data-testid="text-no-clients">
                        {searchTerm ? 'No clients match your search' : 'No clients found'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {selectedTab === 'newClients' && (
                <div className="space-y-3">
                  {pendingUsers.map((user) => (
                    <div
                      key={user.id}
                      className={`cursor-pointer p-3 rounded-lg border transition-all duration-200 hover:scale-[1.02] ${selectedPendingUser?.id === user.id
                        ? "bg-gradient-to-r from-[#025951] to-emerald-500 text-white border-[#025951] shadow-lg"
                        : "bg-neutral-50 dark:bg-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-500 border-gray-200 dark:border-neutral-500 text-gray-700 dark:text-gray-300"
                        }`}
                      onClick={() => setSelectedPendingUser(user)}
                      data-testid={`pending-user-card-${user.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate" data-testid={`text-pending-user-name-${user.id}`}>
                          {user.name}
                        </div>
                        <div className={`text-xs truncate ${selectedPendingUser?.id === user.id ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'
                          }`} data-testid={`text-pending-user-email-${user.id}`}>
                          {user.email}
                        </div>
                      </div>
                    </div>
                  ))}

                  {pendingUsers.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p data-testid="text-no-pending-users">No pending registrations</p>
                    </div>
                  )}
                </div>
              )}

              {selectedTab === 'deleteRequests' && (
                <div className="space-y-3">
                  {deleteRequests.length > 0 ? (
                    deleteRequests.map((request) => (
                      <div
                        key={request.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${selectedDeleteRequest?.id === request.id
                          ? "bg-red-100 dark:bg-red-900 text-red-900 dark:text-red-100 border-red-400"
                          : "bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-800/30"
                          }`}
                        onClick={() => setSelectedDeleteRequest(request)}
                      >
                        <p><strong>Client:</strong> {request.name || 'Unknown'}</p>
                        <p><strong>Reason:</strong> {request.reason}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center">No delete requests found.</p>
                  )}

                </div>
              )}

            </div>
          </div>

          {/* Client Details */}
          <div className="flex-1">
            <div className="bg-white dark:bg-neutral-700 rounded-2xl shadow-lg border border-gray-200 dark:border-neutral-500 p-6">
              {selectedClient && selectedTab === 'clients' ? (
                <>
                  {/* Client Info Header */}
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-neutral-500">
                    <div>
                      <h2 className="text-xl font-semibold">{selectedClient.name}</h2>
                      <p className="text-gray-600 dark:text-gray-400">{selectedClient.email}</p>
                      <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium ${selectedClient.isBlocked
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}>
                        {selectedClient.isBlocked ? 'Blocked' : 'Active'}
                      </span>
                    </div>

                    {/* Client Actions */}
                    <div className="flex space-x-2">
                      {/* View Client */}
                      <button
                        onClick={() => setShowClientDetails(selectedClient)}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Client Details
                      </button>

                      {/* Block / Unblock */}
                      <button
                        onClick={() => handleBlockClient(selectedClient.userId)}
                        disabled={isBlocking}
                        className={`px-4 py-2 rounded-lg transition-colors flex items-center ${selectedClient.isBlocked
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                          }`}
                      >
                        <Ban className="w-4 h-4 mr-2" />
                        {isBlocking ? 'Processing...' : selectedClient.isBlocked ? 'Unblock' : 'Block'}
                      </button>

                      {/* Delete Client */}
                      <button
                        onClick={() =>
                          setDeleteConfirmation({
                            type: 'client',
                            id: selectedClient.userId,
                            name: selectedClient.name,
                          })
                        }
                        className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Client
                      </button>
                    </div>
                  </div>

                  {/* Editable User Info */}
                  <div className="bg-neutral-50 dark:bg-neutral-700 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3 flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      Personal Information
                    </h3>
                    <div className="space-y-2 text-sm">
                      <EditableField
                        label="Name"
                        value={selectedClient.name}
                        field="name"
                        clientId={selectedClient.userId}
                        onSave={handleUpdateClient}
                      />
                      <EditableField
                        label="Email"
                        value={selectedClient.email}
                        field="email"
                        clientId={selectedClient.userId}
                        onSave={handleUpdateClient}
                      />
                      <EditableField
                        label="Phone"
                        value={selectedClient.phone || ""}
                        field="phone"
                        onSave={(field, value) => handleUpdateClient(field, value)}
                        inputComponent={(props) => (
                          <UKPhoneInput
                            value={props.value}
                            onChange={(val) => props.onChange({ target: { value: val } } as any)}
                          />
                        )}
                      />



                      <EditableField
                        label="Address"
                        value={selectedClient.address || ""}
                        field="address"
                        clientId={selectedClient.userId}
                        onSave={handleUpdateClient}
                      />

                      <EditableField
                        label="Password"
                        value={selectedClient.password || ""}
                        field="password"
                        clientId={selectedClient.userId}
                        onSave={handleUpdateClient}
                        isPassword={true}
                      />


                      <div><span className="font-medium">Role:</span> {selectedClient.role}</div>
                      <div>
                        <span className="font-medium flex items-center">
                          <Calendar className="w-4 h-4 mr-1" /> Member since:
                        </span>
                        {selectedClient.createdAt ? new Date(selectedClient.createdAt).toLocaleDateString() : "Unknown"}
                      </div>
                      <div>
                        <span className="font-medium">Status:</span>
                        <span
                          className={`ml-2 px-2 py-1 rounded-full text-xs ${selectedClient.isBlocked
                            ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            }`}
                        >
                          {selectedClient.isBlocked ? "Blocked" : "Active"}
                        </span>
                      </div>

                      {/* CÂMP NOTIȚE NOU - adaugă după Status */}
                      <EditableField
                        label="Admin Notes"
                        value={selectedClient.notes || ""}
                        field="notes"
                        clientId={selectedClient.userId}
                        onSave={handleUpdateClient}
                        isTextarea={true}
                      />

                    </div>
                  </div>



                  {/* Orders List */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4" data-testid="heading-client-orders">
                      Orders ({selectedClient.orders.length})
                    </h3>

                    {selectedClient.orders.length > 0 ? (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {selectedClient.orders.map((order) => (
                          <div
                            key={order.id}
                            className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 rounded-lg"
                            data-testid={`order-card-${order.id}`}
                          >
                            <div className="flex-1">
                              <div className="font-medium" data-testid={`text-order-ref-${order.id}`}>
                                {order.orderRef}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                {order.jobType} • {new Date(order.orderDate).toLocaleDateString()}
                              </div>
                            </div>

                            <div className="flex items-center space-x-3">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${order.status === 'Dispatched'
                                  ? 'bg-green-100 text-green-800'
                                  : order.status === 'In process'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-blue-100 text-blue-800'
                                  }`}
                                data-testid={`badge-order-status-${order.id}`}
                              >
                                {order.status}
                              </span>

                              {/* 🆕 Delete order button */}
                              <button
                                onClick={() => handleDeleteOrder(order.id, selectedClient.userId)}
                                className="inline-flex items-center px-3 py-1 text-sm rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
                                aria-label={`Delete order ${order.orderRef}`}
                                title="Delete order"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </button>
                            </div>

                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p data-testid="text-no-orders">No orders found for this client</p>
                      </div>
                    )}
                  </div>
                </>
              ) : selectedPendingUser && selectedTab === 'newClients' ? (
                <>
                  {/* Pending User Details */}
                  <div className="text-center mb-6">
                    <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-2">
                      User Registration Details
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      Review and approve or reject this registration
                    </p>
                  </div>

                  {/* User Information */}
                  <div className="bg-neutral-50 dark:bg-neutral-600 p-6 rounded-lg mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left Column: User Info */}
                      <div>
                        <h3 className="font-semibold mb-4 flex items-center">
                          <Mail className="w-4 h-4 mr-2" />
                          User Information
                        </h3>

                        <div className="space-y-3">
                          {/* Name */}
                          <EditableField
                            label="Name"
                            value={selectedPendingUser?.name}
                          />


                          {/* Email */}
                          <EditableField label="Email" value={selectedPendingUser.email} />

                          {/* Postcode */}
                          <EditableField label="Postcode" value={selectedPendingUser.postcode} />

                          {/* City */}
                          <EditableField label="City" value={selectedPendingUser.city} />

                          {/* Registration Date */}
                          <EditableField
                            label="Registration Date"
                            value={new Date(selectedPendingUser.createdAt).toLocaleDateString()}
                          />
                        </div>
                      </div>

                      {/* Right Column: Status */}
                      <div>
                        <h3 className="font-semibold mb-4">Status</h3>
                        <div className="bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" />
                              <span className="font-medium text-yellow-800 dark:text-yellow-200">
                                Pending Approval
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                            This user is waiting for admin approval
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-center space-x-4 pt-4 border-t border-gray-200 dark:border-neutral-600">
                    <button
                      onClick={() => handleApproveUser(selectedPendingUser.id)}
                      disabled={isApproving === selectedPendingUser.id}
                      className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center disabled:opacity-50"
                      data-testid={`button-approve-user-${selectedPendingUser.id}`}
                    >
                      <UserCheck className="w-5 h-5 mr-2" />
                      {isApproving === selectedPendingUser.id ? 'Approving...' : 'Approve User'}
                    </button>

                    <button
                      onClick={() => handleRejectUser(selectedPendingUser.id)}
                      disabled={isRejecting === selectedPendingUser.id}
                      className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center disabled:opacity-50"
                      data-testid={`button-reject-user-${selectedPendingUser.id}`}
                    >
                      <UserX className="w-5 h-5 mr-2" />
                      {isRejecting === selectedPendingUser.id ? 'Rejecting...' : 'Reject User'}
                    </button>
                  </div>
                </>
              ) : selectedDeleteRequest && selectedTab === 'deleteRequests' ? (
                <>
                  {/* Delete Request Details */}
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-2">
                      Delete Request Details
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      Review this request carefully before confirming deletion.
                    </p>
                  </div>

                  {/* Client Information */}
                  <div className="bg-neutral-300 dark:bg-neutral-600 p-4 rounded-lg">
                    <p><strong>Name:</strong> {selectedDeleteRequest.name || "N/A"}</p>
                    <p><strong>Email:</strong> {selectedDeleteRequest.email || "N/A"}</p>
                    <p><strong>Reason:</strong> {selectedDeleteRequest.reason}</p>
                    {selectedDeleteRequest.custom_reason && (
                      <p><strong>Additional Info:</strong> {selectedDeleteRequest.custom_reason}</p>
                    )}
                  </div>

                  {/* Orders */}
                  <div className="bg-neutral-300 dark:bg-neutral-600 p-4 rounded-lg mt-4">
                    <h3 className="text-lg font-semibold mb-2">Orders</h3>
                    {selectedDeleteRequest.orders && selectedDeleteRequest.orders.length > 0 ? (
                      selectedDeleteRequest.orders.map((order: any) => (
                        <div key={order.orderid} className="border-b border-neutral-500 py-2">
                          <p><strong>Order Ref:</strong> {order.orderref}</p>
                          <p><strong>Status:</strong> {order.orderstatus}</p>
                          <p><strong>Date:</strong> {new Date(order.orderdate).toLocaleDateString()}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-400">No orders found for this client.</p>
                    )}
                  </div>

                  {/* Confirm Delete Button */}
                  <button
                    onClick={() => {
                      console.log("Deleting user with ID:", selectedDeleteRequest.userId);
                      handleDeleteClient(selectedDeleteRequest.userId);
                    }}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors mt-4"
                  >
                    Confirm Delete
                  </button>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2" data-testid="heading-select-client">
                    {selectedTab === 'clients'
                      ? 'Select a client to view details'
                      : 'Select a pending user to review'}
                  </h3>
                  <p className="text-sm" data-testid="text-client-instructions">
                    {selectedTab === 'clients'
                      ? 'Choose a client from the list to see their orders and manage their account'
                      : 'Click on a pending registration from the list to review and approve/reject'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Client Details Modal */}
      {showClientDetails && (
        <ClientDetailsModal
          client={showClientDetails}
          onClose={() => setShowClientDetails(null)}
          onBlock={handleBlockClient}
          onDelete={(clientId) => {
            setDeleteConfirmation({
              type: 'client',
              id: clientId,
              name: showClientDetails.name,
            });
            setShowClientDetails(null);
          }}
        />
      )}


      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mr-4">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Confirm Deletion
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold">{deleteConfirmation.name}</span>?
              {deleteConfirmation.type === 'client' &&
                " This will also delete all associated orders."
              }
            </p>

            <div className="flex space-x-3">
              <button
                data-testid="button-cancel-delete"
                onClick={() => setDeleteConfirmation(null)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="button-confirm-delete"
                onClick={() => {
                  if (deleteConfirmation.type === 'client') {
                    handleDeleteClient(deleteConfirmation.id);
                  }
                }}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pentru crearea clientului nou */}
      {showCreateClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Create New Client</h2>
              <button onClick={() => setShowCreateClientModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  value={newClientData.name}
                  onChange={(e) => setNewClientData({ ...newClientData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-neutral-700 dark:border-neutral-600"
                  placeholder="Client name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  value={newClientData.email}
                  onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-neutral-700 dark:border-neutral-600"
                  placeholder="client@example.com"
                />
              </div>

              {/* Password cu ochi */}
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={newClientData.password}
                  onChange={(e) => setNewClientData({ ...newClientData, password: e.target.value })}
                  className="w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>


              {/* Phone */}
              <div>
                <label className="block text-sm font-medium mb-2">Phone</label>
                <input
                  type="tel"
                  value={newClientData.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\s/g, ''); // elimină spațiile
                    setNewClientData({ ...newClientData, phone: val });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-neutral-700 dark:border-neutral-600"
                  placeholder="+44 123 456 7890"
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium mb-2">Address</label>
                <textarea
                  value={newClientData.address}
                  onChange={(e) => setNewClientData({ ...newClientData, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-neutral-700 dark:border-neutral-600"
                  rows={3}
                  placeholder="Client address"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button onClick={() => setShowCreateClientModal(false)} className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors" disabled={isCreatingClient}>
                Cancel
              </button>
              <button
                onClick={handleCreateClient}
                disabled={isCreatingClient}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isCreatingClient ? 'Creating...' : 'Create Client'}
              </button>
            </div>
          </div>
        </div>
      )}


      <div className="fixed bottom-6 right-6">
        <button
          onClick={() => navigate("/admin")}
          className="px-6 py-3 bg-gradient-to-r from-[#025951] to-emerald-500 text-white rounded-full shadow-lg hover:from-emerald-600 hover:to-emerald-400 transition-all duration-200 flex items-center gap-2"
        >
          ← Back to Dashboard
        </button>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

    </Layout>
  );
};

export default ManageClients;